/**
 * src/components/shared/breadcrumb/EntityUpdateDiff.jsx — TICKET-1440 S6.
 *
 * A pending update_entity action (a later note on an existing deal) shown as a small diff:
 * one "field: from -> to" line per change, with accept (apply the diff) and dismiss.
 * The server refuses (409) when a value changed meanwhile; the caller's toast shows it.
 */
import { Check, X, ArrowRight } from 'lucide-react';
import { useT } from '../../../lib/i18n/I18nContext';

const btn = {
  width: 44, minHeight: 44, height: 44, padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

export default function EntityUpdateDiff({ action, busy = false, onAccept, onDismiss }) {
  const { t } = useT();
  const d = action.detail_json || {};
  const unsure = new Set(d.to_verify || []);
  const value = (v) => (v == null || v === '' ? t('breadcrumbCard.entityUpdate.empty') : String(v));
  const entityLabel = t(`breadcrumbCard.entityUpdate.entity.${d.entity}`, { defaultValue: d.entity || '' });

  return (
    <div data-testid="entity-update-diff" style={{
      display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 20px',
      background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>
          {t('breadcrumbCard.entityUpdate.title', { entity: entityLabel, name: d.target_label || '' })}
        </div>
        <button type="button" data-testid="entity-update-diff-accept" disabled={busy}
          onClick={(e) => { e.stopPropagation(); onAccept(action); }}
          title={t('breadcrumbCard.entityUpdate.apply')} aria-label={t('breadcrumbCard.entityUpdate.apply')}
          style={{ ...btn, background: 'var(--ok-tint)', color: 'var(--ok)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>
          <Check size={16} />
        </button>
        <button type="button" data-testid="entity-update-diff-dismiss"
          onClick={(e) => { e.stopPropagation(); onDismiss(action.id); }}
          title={t('breadcrumbCard.entityUpdate.dismiss')} aria-label={t('breadcrumbCard.entityUpdate.dismiss')}
          style={{ ...btn, background: 'var(--bad-tint)', color: 'var(--bad)' }}>
          <X size={16} />
        </button>
      </div>
      {Object.entries(d.changes || {}).map(([col, c]) => (
        <div key={col} data-testid="entity-update-diff-change"
          style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, fontSize: 13, color: 'var(--t2)' }}>
          <span style={{ fontWeight: 600 }}>{t(`breadcrumbCard.entityUpdate.field.${col}`, { defaultValue: col })}:</span>
          <span style={{ textDecoration: c?.from == null || c?.from === '' ? 'none' : 'line-through' }}>{value(c?.from)}</span>
          <ArrowRight size={12} aria-hidden="true" />
          <span style={{ color: 'var(--t1)', fontWeight: 600 }}>{value(c?.to)}</span>
          {unsure.has(col) && (
            <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, background: 'var(--warn-tint)', color: 'var(--warn)' }}>
              {t('breadcrumbCard.entityUpdate.toVerify')}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
